import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaWhatsapp,
} from "react-icons/fa";
import Header from "../components/Header";
import Lottie from "lottie-react";
import hotelAnimation from "../assets/animations/register-animation.json";
import Swal from "sweetalert2";
import axios from "axios";
import "../css/Register.css";

const Register = () => {
  const navigate = useNavigate();
  const [agree, setAgree] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone_number: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!agree) {
      Swal.fire({
        icon: "warning",
        title: "Terms & Conditions",
        text: "You must agree to the Terms & Conditions!",
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Password Mismatch",
        text: "Passwords do not match!",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_APP_URL}/register`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Platform": "web",
            "API-Key": process.env.REACT_APP_API_KEY,
            "X-API-Version": process.env.REACT_APP_X_API_VERSION,
          },
        },
      );

      Swal.fire({
        icon: "success",
        title: "Registration Successful!",
        text: response.data.message,
        timer: 2500,
        showConfirmButton: false,
      }).then(() => {
        navigate("/verify-otp", { state: { phone: formData.phone_number } });
      });
    } catch (error) {
      let errorMessage = "Registration failed. Please try again.";

      if (error.response?.data?.errors) {
        const messages = Object.values(error.response.data.errors).flat();
        errorMessage = messages.join("<br>");
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      Swal.fire({
        icon: "error",
        title: "Registration Failed",
        html: `<strong>${errorMessage}</strong>`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-page">
      <Header />
      <Container fluid className="register-container">
        <Row className="g-0">
          <Col md={6} className="image-section">
            <div className="overlay"></div>
            <div className="lottie-container">
              <Lottie animationData={hotelAnimation} loop={true} />
            </div>
          </Col>

          <Col
            md={6}
            className="form-section d-flex align-items-center justify-content-center"
          >
            <Card className="form-card p-4 shadow-lg">
              <h3 className="mb-2 text-center animate-fade-in">
                Create An Account
              </h3>
              <Form onSubmit={handleSubmit} className="animate-slide-in">
                <Form.Group className="mb-3">
                  <Form.Label>
                    Name <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaUser />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="name"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    WhatsApp Number <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaWhatsapp style={{ color: "#25D366" }} size={22} />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="phone_number"
                      placeholder="e.g. 081234567890"
                      value={formData.phone_number}
                      onChange={handleChange}
                      required
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email (optional)</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaEnvelope />
                    </InputGroup.Text>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Password <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaLock />
                    </InputGroup.Text>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                    <InputGroup.Text
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ cursor: "pointer" }}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </InputGroup.Text>
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Confirm Password <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaLock />
                    </InputGroup.Text>
                    <Form.Control
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Re-enter your password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                    <InputGroup.Text
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </InputGroup.Text>
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Check
                    type="checkbox"
                    label={
                      <>
                        I agree with{" "}
                        <Link to="/terms">Terms &amp; Conditions</Link>
                      </>
                    }
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                  />
                </Form.Group>

                <Button variant="primary" type="submit" className="w-100 mb-3">
                  {loading ? "Signing Up..." : "Sign Up"}
                </Button>

                <Button
                  variant="outline-primary"
                  className="w-100"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </Button>
              </Form>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Register;
